import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';
import { FaUndo, FaFilter } from 'react-icons/fa';
import Axios from 'axios';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import RelationListPicker from '@/shared/components/input/RelationListPicker';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import MultiSelect, {
  addItem as addMultiSelectItem,
  removeItem as removeMultiSelectItem,
  splitItems as splitMultiSelectItems,
} from '@/shared/components/input/multiselect/MultiSelect';
import LegalFormMultiSelect from '@/shared/components/input/multiselect/LegalFormMultiSelect';

import AuthService from '@/shared/services/AuthService';
import UserSearch from '@/shared/components/input/UserSearch';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import RelationTypeIcon from '@/shared/components/RelationTypeIcon';
import enumCache from '@/shared/services/EnumCacheService';
import DataCacheService from '@/shared/services/DataCacheService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { crmTypeSorter } from '@/shared/utils/sorters';
import RelationSearch from '@/shared/components/input/RelationSearch';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import EnumMultiSelect from '@/shared/components/input/multiselect/EnumMultiSelect';
import AssignmentTemplateSelector from '@/shared/components/input/AssignmentTemplateSelector';
import { hasAssignments } from '@/shared/components/security/HasAssignments';

import guid from '@/shared/utils/guid';
import messages from './taskScheduleMessages';
import RelationGroupMultiSelect from '@/shared/components/input/multiselect/RelationGroupMultiSelect';
import employeeProfileMessages from '@/shared/messages/employeeProfileMessages';
import DynamicFilter, {
  getDynamicFiltersFromDefaultFilters,
  initDynamicFilters,
} from '@/shared/components/DynamicFilter';
import { Dropdown } from 'react-bootstrap';
import CheckboxDropdownMenu from '@/shared/components/input/CheckboxDropdownMenu';
import userStorage from '@/shared/services/userStorage';

const dynamicFiltersStorageKey = 'TaskScheduleOverviewDynamicFilters';

export const defaultFilters = {
  relationId: null,
  relationName: null,
  invoiceStreamId: null,
  assistant: null, // UserId
  responsible: null, // UserId
  relationTypes: [], // Array
  crmTypes: [3], // Array
  ownCompanies: [], // Array
  vatPeriodicities: [],
  assignmentTemplateId: null,
  legalForms: [],
  relationGroups: [],
  assistantBackup: null,
  associate: null,
  billingUser: null,
  seniorUser: null,
  withLabels: [],
  withoutLabels: [],
  linkType: null,
  linkTypeReverse: null,
  filterBehaviours: {
    withLabels: 1,
  },
  relations: [],
};

class TaskScheduleOverviewFilters extends PureComponent {
  state = {
    crmTypes: [],
    relationTypes: [],
    linkTypes: [],
    dynamicFilters: [],
    selectedRelations: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      relationId: PropTypes.string,
      relationName: PropTypes.string,
      invoiceStreamId: PropTypes.string,
      assistant: PropTypes.string, // UserId
      responsible: PropTypes.string, // UserId
      relationTypes: PropTypes.arrayOf(PropTypes.number),
      crmTypes: PropTypes.arrayOf(PropTypes.number),
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      withLabels: PropTypes.arrayOf(PropTypes.string),
      withoutLabels: PropTypes.arrayOf(PropTypes.string),
      linkType: PropTypes.number,
      linkTypeReverse: PropTypes.number,
      vatPeriodicities: PropTypes.arrayOf(PropTypes.number),
      assignmentTemplateId: PropTypes.string,
      legalForms: PropTypes.arrayOf(PropTypes.number),
      relationGroups: PropTypes.arrayOf(PropTypes.string),
      relations: PropTypes.arrayOf(PropTypes.string),
      assistantBackup: PropTypes.string, // UserId
      associate: PropTypes.string, // UserId
      billingUser: PropTypes.string, // UserId
      seniorUser: PropTypes.string, // UserId
    }),
    onResetFilters: PropTypes.func.isRequired,
    countSelected: PropTypes.string,
    hasFilters: PropTypes.bool,
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    filters: defaultFilters,
    countSelected: '',
    hasFilters: false,
  };

  componentDidMount() {
    const { intl, filters } = this.props;

    const relationLabelsPromise = api.base.get(`${config.appUrls.api}/api/Resource/Tags`);
    const relationsPromise =
      filters.relations?.length > 0
        ? api.base.post(`${config.appUrls.api}/api/relation/names`, filters.relations)
        : Promise.resolve({ data: [] });

    Promise.all([
      enumCache.getTranslated('relationtype', intl, true),
      enumCache.getTranslated('crmtype', intl, crmTypeSorter),
      relationLabelsPromise,
      enumCache.get('linktype'),
      relationsPromise,
    ]).then(([relationTypes, crmTypes, { data: labels }, linkTypes, { data: relationNames }]) => {
      const allDynamicFilters = [
        ...getDynamicFiltersFromDefaultFilters(defaultFilters, 'crmTypes', 'relationTypes'),
        { id: 'linkType', label: messages.LinkType },
        { id: 'linkTypeReverse', label: messages.LinkTypeReverse },
        { id: 'relationPicker', label: messages.RelationPicker },
      ];

      this.setState({
        relationTypes,
        crmTypes,
        relationLabels: labels
          .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
          .sort(dynamicSort('sort')),
        linkTypes: linkTypes
          .map(x => ({
            id: x.key,
            label: intl.formatMessage(listItemMsg('linktypeto', x.key, x.value)),
          }))
          .sort(dynamicSort('label')),
        linkTypesReverse: linkTypes
          .map(x => ({
            id: x.key,
            label: intl.formatMessage(listItemMsg('linktypeback', x.key, x.value)),
          }))
          .sort(dynamicSort('label')),
        dynamicFilters: initDynamicFilters(
          intl,
          allDynamicFilters,
          userStorage.getItem(dynamicFiltersStorageKey) || [],
          filters,
          defaultFilters,
        ),
        selectedRelations: relationNames ?? [],
      });
    });
  }

  componentDidUpdate(prevProps) {
    // Reset selectedRelations state when relations filter is cleared
    if (prevProps.filters.relations?.length > 0 && (!this.props.filters.relations || this.props.filters.relations.length === 0)) {
      this.setState({ selectedRelations: [] });
    }
  }

  dynamicFilterChanged = (item, selected, visibleItemIds) => {
    const { dynamicFilters } = this.state;
    const { filters } = this.props;

    const newFilters = dynamicFilters.map(x => {
      if (x.id !== item.id && item.id !== 'selectall') return x;
      if (visibleItemIds && !visibleItemIds.includes(x.id)) return x;
      return { ...x, selected };
    });

    userStorage.setItem(
      dynamicFiltersStorageKey,
      newFilters.filter(x => x.selected).map(x => x.id),
    );
    this.setState({
      dynamicFilters: newFilters,
    });

    if (!selected && JSON.stringify(filters[item.id]) !== JSON.stringify(defaultFilters[item.id])) {
      // Reset filter when it's removed
      this.filterChanged({ [item.id]: defaultFilters[item.id] });
    }
  };

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };
    if (filters !== newFilters) onFilterChange(newFilters);
  };

  responsibleChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ responsible: null });
    } else {
      this.filterChanged({ responsible: item.id });
    }
  };

  linkTypeChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ linkType: null });
    } else {
      this.filterChanged({ linkType: item.id });
    }
  };

  assistantChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ assistant: null });
    } else {
      this.filterChanged({ assistant: item.id });
    }
  };
  filterRelationTypes = relType => {
    const { filters = {} } = this.props;
    const types = filters.relationTypes || [];

    const idx = types.indexOf(relType);
    if (idx >= 0) {
      this.filterChanged({ relationTypes: types.filter((_, i) => i !== idx) });
    } else {
      this.filterChanged({ relationTypes: [...types, relType] });
    }
  };

  filterCrmTypes = crmType => {
    const { filters = {} } = this.props;
    const types = filters.crmTypes || [];

    const idx = types.indexOf(crmType);
    if (idx >= 0) {
      this.filterChanged({ crmTypes: types.filter((_, i) => i !== idx) });
    } else {
      this.filterChanged({ crmTypes: [...types, crmType] });
    }
  };

  relationChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ relationId: null, relationName: null });
    } else {
      this.filterChanged({
        relationId: item.key,
        relationName: item.label,
      });
    }
  };

  renderDynamicFilter = ({ type, filters, intl }) => {
    switch (type) {
      case 'linkType': {
        const { linkTypes } = this.state;

        const initialLinkType = filters.linkType
          ? linkTypes.find(x => x.id === filters.linkType)
          : null;

        return (
          <div className="col-md-6 col-lg-4 margin5B">
            <div className="form-group">
              <select
                className="form-control"
                onChange={e =>
                  this.filterChanged({ linkType: e.target.value ? parseInt(e.target.value) : null })
                }
                value={filters.linkType || ''}
              >
                <option value="">{intl.formatMessage(messages.FilterLinkType)}</option>
                {linkTypes.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }
      case 'linkTypeReverse': {
        const { linkTypesReverse } = this.state;
        return (
          <div className="col-md-6 col-lg-4 margin5B">
            <div className="form-group">
              <select
                className="form-control"
                onChange={e =>
                  this.filterChanged({
                    linkTypeReverse: e.target.value ? parseInt(e.target.value) : null,
                  })
                }
                value={filters.linkTypeReverse || ''}
              >
                <option value="">{intl.formatMessage(messages.FilterLinkTypeReverse)}</option>
                {linkTypesReverse.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }
      case 'relationPicker': {
        const { selectedRelations } = this.state;

        return (
          <div className="col-md-6 col-lg-4 margin5B">
            <div className="form-group">
              <RelationListPicker
                placeholder={intl.formatMessage(messages.ChooseRelations)}
                relations={selectedRelations || []}
                onAddRelation={relation => {
                  this.setState({ selectedRelations: [...selectedRelations, relation] }, () => {
                    this.filterChanged({
                      relations: [...filters.relations, relation.id],
                    });
                  });
                }}
                onRemoveRelation={relation => {
                  this.setState(
                    { selectedRelations: selectedRelations.filter(x => x.id !== relation.id) },
                    () => {
                      this.filterChanged({
                        relations: filters.relations.filter(x => x !== relation.id),
                      });
                    },
                  );
                }}
              />
            </div>
          </div>
        );
      }
      default:
        console.warn(`Missing filter: ${type}`);
        return null;
    }
  };

  render() {
    const { relationTypes, crmTypes, relationLabels, dynamicFilters } = this.state;
    const { filters, intl, countSelected, onResetFilters, hasFilters } = this.props;
    const defaultBtnClass = 'btn btn-default';

    const showCompanies = hasMultipleCompanies();
    const initRelation = filters.relationId
      ? {
          key: filters.relationId,
          label: filters.relationName,
          invoiceStreamId: filters.invoiceStreamId,
        }
      : null;

    const [withLabelsSelection, withLabelsAvailable] = splitMultiSelectItems(
      relationLabels,
      filters.relationTags,
      'label',
    );
    const [withoutLabelsSelection, withoutLabelsAvailable] = splitMultiSelectItems(
      relationLabels,
      filters.withoutRelationTags,
      'label',
    );

    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);
    const dropdownFilters = [
      {
        id: 'selectall',
        label: intl.formatMessage(messages.SelectAll),
        selected: !dynamicFilters.some(x => !x.selected),
      },
      ...dynamicFilters,
    ];

    return (
      <div className="info-block container-fluid radius4B">
        <div className="row">
          <div className="col-lg-4 col-sm-12 margin10B">
            <RelationSearch onChange={this.relationChanged} initialSelectedItem={initRelation} />
          </div>
          <div className="col-lg-4 col-sm-6 margin10B">
            <div role="group" aria-label="relationType" className="btn-group btn-group-justified">
              {relationTypes.map(t => (
                <div className="btn-group" key={`RelTypeFilter${t.id}`} role="group">
                  <button
                    onClick={() => {
                      document.activeElement.blur();
                      this.filterRelationTypes(t.id);
                    }}
                    type="button"
                    title={t.label}
                    className={classnames(defaultBtnClass, {
                      active:
                        filters && filters.relationTypes && filters.relationTypes.includes(t.id),
                    })}
                  >
                    <RelationTypeIcon type={t.id} />
                    {t.label}
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="col-lg-4 col-sm-6 margin10B">
            <div role="group" aria-label="crmType" className="btn-group btn-group-justified">
              {crmTypes.map(t => (
                <div className="btn-group" key={`RelCrmFilter${t.id}`} role="group">
                  <button
                    onClick={() => {
                      document.activeElement.blur();
                      this.filterCrmTypes(t.id);
                    }}
                    type="button"
                    title={t.label}
                    className={classnames(defaultBtnClass, {
                      active: filters && filters.crmTypes && filters.crmTypes.includes(t.id),
                    })}
                  >
                    {t.label}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        {visibleFilters && visibleFilters.length > 0 ? (
          <div className="row hidden-xs hidden-sm">
            {visibleFilters.map(id => (
              <DynamicFilter
                key={id}
                type={id}
                intl={intl}
                filters={filters}
                collClass="col-md-6 col-lg-4 margin5B"
                filterChanged={this.filterChanged}
                labels={relationLabels}
                renderCustomFilter={this.renderDynamicFilter}
              />
            ))}
          </div>
        ) : null}
        <div className="row hidden-xs hidden-sm">
          <div className="col-sm-12">
            <Dropdown id="dynamicFilterMenu">
              <Dropdown.Toggle title={intl.formatMessage(messages.SelectYourFilters)}>
                <FaFilter className="react-icon" />
                <FormattedMessage {...messages.MoreFilters} />
              </Dropdown.Toggle>
              <CheckboxDropdownMenu
                bsRole="menu"
                items={dropdownFilters}
                onItemChanged={this.dynamicFilterChanged}
              />
            </Dropdown>
            {hasFilters && (
              <button
                type="button"
                className="btn btn-default btn-reset margin10L"
                onClick={() => onResetFilters()}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            )}
            <div className="margin-10B content-right pull-right">
              <span>{countSelected}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(TaskScheduleOverviewFilters);
